import './index.scss';
import React, { useState, useRef, RefObject, useEffect } from 'react';
import { KEY_CODE } from '@umu/umu-types';
import { $document, classSet } from '@umu/umu-dom';
import { HoverTooltip } from '../../../hoverTooltip/hoverTooltip';
import { getLengthChar } from '@umu/umu-util';
import InfiniteScroll from '../../../@infiniteScroll/infiniteScroll';
import { getCharacterLength } from '../../../../@utils';
import { If, ListLoading } from 'common/src/components';
import { useDebounceFn, useLockFn } from 'ahooks';

interface IInput {
    placeholder?: string;
    maxLength?: number;
}
interface IProps {
    id?: string;
    defaultValue?: string;
    type?: string;
    disabled?: boolean;
    className?: string;
    onHalfWidthCheck?: boolean;
    autoFocus?: boolean;
    option: IInput;
    onKeyDownEnter?: (value: string) => void;
    onBlur?: () => void;
    onChange?: (value: string) => void;
}
interface ISearchProps {
    placeholder?: string;
    maxLength?: number;
    type?: string;
    isHideBorder?: boolean;
    defaultValue?: string;
    className?: string;
    maxCount?: number;
    pageInfo?: IPageInfo;
    list?: IListItem[];
    hideOnMouseLeave?: boolean;
    getList: (val: string, page?: number) => Promise<IListItem[]>;
    selectList: IListItem[];
    renderData: (data: IListItem, num: number) => JSX.Element;
    renderChildren: (data: IListItem) => JSX.Element;
    onSelect: (data: IListItem) => void;
}
interface IListItem {
    icon: string;
    title: string;
    label: string;
    id: string;
}

export const InputSearch = (props: ISearchProps) => {
    const {
        placeholder = '',
        maxLength,
        type = 'text',
        isHideBorder = false,
        className = '',
        selectList,
        defaultValue = '',
        pageInfo,
        list: defaultList = [],
        hideOnMouseLeave = true,
    } = props;
    const inputRef: RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [viewport, setViewport] = useState<Element | undefined>(undefined);
    const [scroll, setScroll] = useState<Element | undefined>(undefined);
    const [active, setActive] = useState(false);
    const [testValue, setTestValue] = useState(defaultValue);
    const [ulIsShow, setUlIsShow] = useState(false);
    const [list, setList] = useState(defaultList);
    useEffect(() => {
        const viewportEl = $document.querySelector('.list-table') || undefined;
        const scrollEl = $document.querySelector('.list-table') || undefined;
        setViewport(viewportEl);
        setScroll(scrollEl);
    }, []);
    useEffect(() => {
        if (defaultValue === '') {
            setList(defaultList);
            setTestValue('');
        }
    }, [defaultValue]);
    const onKeyDown = (e: React.KeyboardEvent) => {
        if (!inputRef.current) {
            return;
        }
        const keyCode = e.keyCode;
        if (keyCode === KEY_CODE.KEY_ENTER) {
            return false;
        }
        return true;
    };

    const { run: getList } = useDebounceFn(
        async (values: string) => {
            const res = await props.getList(values);
            setList(res);
            setUlIsShow(true);
        },
        { wait: 500 },
    );

    const onChange = useLockFn(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const el = e.currentTarget;
        if (!el) {
            return;
        }
        const values: string = el.value;
        setTestValue(values);
        await getList(values);
    });

    const liOnClick = (item: IListItem) => {
        props.onSelect(item);
        setTestValue('');
        setUlIsShow(false);
    };
    const getLoadMore = async () => {
        setIsLoading(true);
        if (!pageInfo) {
            return;
        }
        if (pageInfo.total_page_num > pageInfo.current_page) {
            if (props.getList) {
                setIsLoading(true);
                const res = await props.getList(testValue, pageInfo.current_page + 1);
                setList(list.concat(res || []));
                setIsLoading(false);
            }
        }
    };
    return (
        <div className={className + ' umu-input'} onMouseLeave={() => hideOnMouseLeave && setUlIsShow(false)}>
            <HoverTooltip hideArrow title={!testValue && !selectList.length ? placeholder : ''} placement="bottom">
                <div
                    className={classSet({
                        'select-list': true,
                        'active-input': ulIsShow,
                        'active': ulIsShow || active,
                        'border': !isHideBorder,
                    })}
                    id="component-choose-user-name-input"
                >
                    {selectList.map((data: IListItem, index: number) => props.renderData(data, index))}
                    <If condition={!props.maxCount || props.maxCount > selectList.length}>
                        <span className="input-wrap">
                            <input
                                ref={inputRef}
                                type={type}
                                className="input"
                                onFocus={() => setActive(true)}
                                onBlur={() => setActive(false)}
                                placeholder={placeholder}
                                autoFocus
                                maxLength={maxLength}
                                onKeyDown={onKeyDown}
                                onChange={onChange}
                                defaultValue={testValue}
                            />
                        </span>
                    </If>
                </div>
            </HoverTooltip>

            <div
                className="list"
                style={{ display: ulIsShow && list.length > 0 && testValue !== '' ? 'block' : 'none' }}
            >
                <InfiniteScroll
                    load={getLoadMore}
                    hasMore={!!pageInfo && !!pageInfo.total_page_num && pageInfo.total_page_num > pageInfo.current_page}
                    viewportSelector={viewport}
                    scrollerSelector={scroll}
                    isLoading={isLoading}
                    bottomDistance={70}
                    scrollThreshold={500}
                >
                    <ul className="list-table">
                        {list.map((data: IListItem) => {
                            return (
                                <li
                                    key={data.id + '-' + data.title}
                                    onClick={() => {
                                        liOnClick(data);
                                    }}
                                    className="children-item"
                                >
                                    {props.renderChildren(data)}
                                </li>
                            );
                        })}
                        {isLoading && <ListLoading />}
                    </ul>
                </InfiniteScroll>
            </div>
        </div>
    );
};

export const Input = (props: IProps) => {
    const {
        defaultValue = '',
        type = 'text',
        className = '',
        disabled = false,
        option: { maxLength = 120, placeholder = '' },
        autoFocus = false,
        onHalfWidthCheck = false,
        onChange,
        onBlur,
        onKeyDownEnter,
        id,
    } = props;
    const [value, setValue] = useState(defaultValue);
    const isComposing = useRef(false);
    useEffect(() => {
        setValue(defaultValue);
    }, [defaultValue]);
    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.currentTarget.value || '';
        if (!isComposing.current && onHalfWidthCheck) {
            if (getCharacterLength(value) > maxLength) {
                value = getLengthChar(value, maxLength);
            }
        }
        setValue(value);
        if (onChange) {
            onChange(value);
        }
    };
    const onCompositionStart = () => {
        isComposing.current = true;
    };
    const onCompositionEnd = (e: React.CompositionEvent<HTMLInputElement>) => {
        isComposing.current = false;
        if (onHalfWidthCheck) {
            let newValue = e.currentTarget.value || '';
            if (getCharacterLength(newValue) > maxLength) {
                newValue = getLengthChar(newValue, maxLength);
                setValue(newValue);
                if (onChange) {
                    onChange(newValue);
                }
            }
        }
    };
    const onKeyDown = (e: React.KeyboardEvent) => {
        const keyCode = e.keyCode;
        if (keyCode === KEY_CODE.KEY_ENTER) {
            if (onKeyDownEnter) {
                onKeyDownEnter(value);
            }
            return false;
        }
        return true;
    };
    return (
        <div className={className + ' umu-input'} id={id}>
            <input
                type={type}
                disabled={disabled}
                className="input"
                placeholder={placeholder}
                maxLength={maxLength}
                onKeyDown={onKeyDown}
                onChange={onInputChange}
                onCompositionStart={onCompositionStart}
                onCompositionEnd={onCompositionEnd}
                value={value}
                onBlur={onBlur}
                autoFocus={autoFocus}
            />
        </div>
    );
};
